import React from 'react';
import {
    Box,
    Grid,
    Tooltip,
    Chip,
    IconButton,
    CircularProgress
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

export const getIconButtonStyles = (theme) => ({
    base: {
        width: '42px',
        height: '42px',
        transition: 'all 0.2s ease',
    },
    delete: {
        enabled: {
            backgroundColor: '#ef4444',
            color: '#fff',
            boxShadow: '0 2px 8px rgba(239, 68, 68, 0.25)',
            '&:hover': {
                backgroundColor: '#dc2626',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
            },
        },
        disabled: {
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#e5e7eb',
            color: theme.palette.mode === 'dark' ? theme.palette.text.disabled : '#9ca3af',
            boxShadow: 'none',
        }
    },
    primary: {
        enabled: {
            backgroundColor: theme.palette.primary.main,
            color: '#fff',
            boxShadow: `0 2px 8px ${theme.palette.primary.main}40`,
            '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 12px ${theme.palette.primary.main}4D`,
            },
        },
        disabled: {
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#e5e7eb',
            color: theme.palette.mode === 'dark' ? theme.palette.text.disabled : '#9ca3af',
            boxShadow: 'none',
        }
    },
    success: {
        enabled: {
            backgroundColor: '#10b981',
            color: '#fff',
            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.25)',
            '&:hover': {
                backgroundColor: '#059669',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
            },
        },
        disabled: {
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#e5e7eb',
            color: theme.palette.mode === 'dark' ? theme.palette.text.disabled : '#9ca3af',
            boxShadow: 'none',
        }
    },
    warning: {
        enabled: {
            backgroundColor: '#f59e0b',
            color: '#fff',
            boxShadow: '0 2px 8px rgba(245, 158, 11, 0.25)',
            '&:hover': {
                backgroundColor: '#d97706',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
            },
        },
        disabled: {
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#e5e7eb',
            color: theme.palette.mode === 'dark' ? theme.palette.text.disabled : '#9ca3af',
            boxShadow: 'none',
        }
    },
    purple: {
        enabled: {
            backgroundColor: '#8b5cf6',
            color: '#fff',
            boxShadow: '0 2px 8px rgba(139, 92, 246, 0.25)',
            '&:hover': {
                backgroundColor: '#7c3aed',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
            },
        },
        disabled: {
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#e5e7eb',
            color: theme.palette.mode === 'dark' ? theme.palette.text.disabled : '#9ca3af',
            boxShadow: 'none',
        }
    },
    cyan: {
        enabled: {
            backgroundColor: '#06b6d4',
            color: '#fff',
            boxShadow: '0 2px 8px rgba(6, 182, 212, 0.25)',
            '&:hover': {
                backgroundColor: '#0891b2',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(6, 182, 212, 0.3)',
            },
        },
        disabled: {
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#e5e7eb',
            color: theme.palette.mode === 'dark' ? theme.palette.text.disabled : '#9ca3af',
            boxShadow: 'none',
        }
    },
    secondary: {
        enabled: {
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#ffffff',
            border: theme.palette.mode === 'dark' 
                ? `1px solid ${theme.palette.divider}` 
                : '1px solid #e5e7eb',
            color: theme.palette.mode === 'dark' ? theme.palette.text.primary : '#6b7280',
            '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' 
                    ? theme.palette.grey[800] 
                    : '#f9fafb',
                borderColor: theme.palette.mode === 'dark' 
                    ? theme.palette.grey[600] 
                    : '#d1d5db',
                transform: 'translateY(-1px)',
            },
        },
        disabled: {
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#e5e7eb',
            color: theme.palette.mode === 'dark' ? theme.palette.text.disabled : '#9ca3af',
            border: theme.palette.mode === 'dark' 
                ? `1px solid ${theme.palette.divider}` 
                : '1px solid #e5e7eb',
        }
    }
});

export const getChipStyles = (theme) => ({
    full: (hasCount) => ({
        backgroundColor: hasCount 
            ? `${theme.palette.primary.main}14` 
            : (theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#f9fafb'),
        border: `1px solid ${hasCount 
            ? `${theme.palette.primary.main}80` 
            : (theme.palette.mode === 'dark' ? theme.palette.divider : '#e5e7eb')}`,
        fontSize: '0.875rem',
        fontWeight: hasCount ? 600 : 400,
        height: '36px',
        flexShrink: 0,
        px: 2.5,
        transition: 'all 0.2s ease',
        '& .MuiChip-label': {
            color: hasCount 
                ? theme.palette.primary.dark 
                : (theme.palette.mode === 'dark' ? theme.palette.text.secondary : '#6b7280')
        },
        '& .MuiChip-icon': {
            margin: 0
        }
    }),
    compact: (hasCount) => ({
        backgroundColor: hasCount 
            ? theme.palette.primary.main 
            : (theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#e5e7eb'),
        color: hasCount 
            ? '#fff' 
            : (theme.palette.mode === 'dark' ? theme.palette.text.primary : '#6b7280'),
        fontSize: '0.875rem',
        fontWeight: 600,
        height: '36px',
        minWidth: '50px',
        flexShrink: 0,
        px: 1.5,
        transition: 'all 0.2s ease',
        '& .MuiChip-icon': {
            margin: 0,
            color: hasCount ? '#fff' : (theme.palette.mode === 'dark' ? theme.palette.text.primary : '#6b7280')
        }
    })
});

// 공통 컨테이너 스타일 (테마에 맞게 동적으로 생성)
export const getContainerStyles = (theme) => ({
    mb: 3,
    p: 2.5,
    borderRadius: 2,
    background: theme.palette.mode === 'dark' 
        ? `linear-gradient(135deg, ${theme.palette.grey[800]} 0%, ${theme.palette.grey[900]} 100%)`
        : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    boxShadow: theme.palette.mode === 'dark' 
        ? '0 1px 3px rgba(0,0,0,0.3)'
        : '0 1px 3px rgba(0,0,0,0.05)',
    position: 'relative',
    border: theme.palette.mode === 'dark' 
        ? `1px solid ${theme.palette.divider}`
        : 'none'
});

// 공통 구분선 컴포넌트
export const Divider = () => {
    const theme = useTheme();
    return (
        <Box
            sx={{
                width: '1px',
                height: '32px',
                backgroundColor: theme.palette.mode === 'dark' ? theme.palette.divider : '#e5e7eb',
                mx: 1
            }}
        />
    );
};

// 공통 선택된 항목 수 표시 Chip
export const SelectionChip = ({ 
    count, 
    showIconButtons, 
    label, 
    noItemsLabel, 
    icon 
}) => {
    const theme = useTheme();
    const chipStyles = getChipStyles(theme);
    
    return (
        <Chip
            icon={icon}
            label={showIconButtons
                ? (count > 0 ? label : noItemsLabel)
                : (count > 0 ? count : '0')
            }
            size="small"
            sx={showIconButtons ? chipStyles.full(count > 0) : chipStyles.compact(count > 0)}
        />
    );
};

// 공통 선택 표시 컴포넌트 (모든 페이지에서 사용)
export const CommonSelectionDisplay = ({ 
    selectedItems, 
    showIconButtons = true,
    customLabel = null,
    customNoItemsLabel = null
}) => {
    const theme = useTheme();
    const defaultLabel = `${selectedItems.length}개 항목 선택됨`;
    const defaultNoItemsLabel = '선택된 항목 없음';
    
    const label = customLabel || defaultLabel;
    const noItemsLabel = customNoItemsLabel || defaultNoItemsLabel;
    
    const icon = selectedItems.length > 0 ? (
        <Box sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: theme.palette.primary.main,
            mr: 0.5
        }} />
    ) : undefined;

    return (
        <SelectionChip
            count={selectedItems.length}
            showIconButtons={showIconButtons}
            label={label}
            noItemsLabel={noItemsLabel}
            icon={icon}
        />
    );
};

// 공통 IconButton 래퍼
export const CommonIconButton = ({ 
    id,
    onClick, 
    disabled, 
    tooltip, 
    children, 
    variant = 'primary',
    customStyles = {}
}) => {
    const theme = useTheme();
    const iconButtonStyles = getIconButtonStyles(theme);
    
    const getVariantStyles = () => {
        if (disabled) {
            return iconButtonStyles[variant]?.disabled || iconButtonStyles.primary.disabled;
        }
        return iconButtonStyles[variant]?.enabled || iconButtonStyles.primary.enabled;
    };

    return (
        <Tooltip title={tooltip}>
            <IconButton
                id={id}
                onClick={onClick}
                disabled={disabled}
                sx={{
                    ...iconButtonStyles.base,
                    ...getVariantStyles(),
                    ...customStyles
                }}
            >
                {children}
            </IconButton>
        </Tooltip>
    );
};

// 메인 CommonTableControls 컴포넌트
const CommonTableControls = ({
    children,
    showIconButtons = true,
    leftContent,
    centerContent,
    rightContent,
    spacing = 2
}) => {
    const theme = useTheme();
    const hasCenter = Boolean(centerContent);
    const leftSize = hasCenter ? (showIconButtons ? 4 : 5) : (showIconButtons ? 8 : 9);
    const rightSize = hasCenter ? (showIconButtons ? 4 : 3) : (showIconButtons ? 4 : 3);
    const centerSize = hasCenter ? (showIconButtons ? 4 : 4) : 0;
    return (
        <Box sx={getContainerStyles(theme)}>
            <Grid container spacing={showIconButtons ? spacing : 1} alignItems="center" sx={{ position: 'relative' }}>
                {leftContent && (
                    <Grid item xs={12} sm={leftSize}>
                        {leftContent}
                    </Grid>
                )}
                {hasCenter && (
                    <Grid item xs={12} sm={centerSize} sx={{ display: 'flex', justifyContent: 'center' }}>
                        {centerContent}
                    </Grid>
                )}
                {rightContent && (
                    <Grid item xs={12} sm={rightSize}>
                        {rightContent}
                    </Grid>
                )}
                {children}
            </Grid>
        </Box>
    );
};

export default CommonTableControls;
